import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { usePayments } from '../hooks/usePayments';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import ReCAPTCHA from 'react-google-recaptcha';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import { getAuthErrorMessage } from '../utils/authErrors';
import Loading from './Loading';
import config from '../config';
import {
    FormSectionStyled,
    ButtonContainerStyled,
    FormContainerStyled,
    InputContainerStyled,
    InputStyled,
    LabelStyled,
    PlansButton,
    PlanViewContainerStyled,
    InputIconStyled,
    ErrorMessageStyled,
    GoogleButtonStyled,
    GoogleButtonContainerStyled,
    OrContainerStyled,
    HrStyled,
} from '../styles/Form';
import { PrimaryButtonStyled } from '../styles/Button';
import { ModalBackgroundStyled, NavLinkWrapper } from '../styles/Main';
import Products from './Products';
import { useLocalStorage } from '../hooks/useLocalStorage';
import iconMap from '../data/iconMap';


const SignUp = ({ plans, setSelectedPlan, selectedPlan }) => {
    const { user, signUp, signInWithGoogle, verificationEmail } = useAuth();
    const { checkout, findPlan } = usePayments();
    const { executeRecaptcha } = useGoogleReCaptcha();
    const navigate = useNavigate();
    const [visited, setVisited] = useLocalStorage("visited", false);
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [secureTextEntry, setSecureTextEntry] = useState(true);
    const [secureTextEntryConfirm, setSecureTextEntryConfirm] = useState(true);
    const [showPlans, setShowPlans] = useState(1);
    const [loading, setLoading] = useState(0);
    const [isFreeTier, setIsFreeTier] = useState(false);
    const [showV2Captcha, setShowV2Captcha] = useState(false);
    const [v2Token, setV2Token] = useState(null);
    const recaptchaV2Ref = useRef(null);

    // Check if user is already authenticated (e.g., Google sign-in without subscription)
    const isAuthenticatedNoSub = user && user.emailVerified;

    // Handle closing the plans modal - navigate back for authenticated users
    const handleClosePlans = (value) => {
        if (value === false && isAuthenticatedNoSub) {
            // Authenticated user closing plans modal - go back to app
            navigate('/');
        } else {
            setShowPlans(value);
        }
    };

    useEffect(() => {
        if(!visited) {
            setVisited(true);
        }
    }, [visited, setVisited])

    const loadCheckout = async (priceId, userId) => {
        let p = findPlan(plans, priceId)
        await checkout(p, userId, '/thankyou', '/signup' )
    }

    const onSelectFree = () => {
        if (isAuthenticatedNoSub) {
            // Already signed in (Google) — go straight to app, free tier auto-provisions
            navigate('/');
        } else {
            // Show signup form for free tier
            setIsFreeTier(true);
            setShowPlans(0);
        }
    };

    const onSelectPlan = (priceId) => {
        setSelectedPlan(priceId);
        setIsFreeTier(false);
        if (isAuthenticatedNoSub) {
            // Already signed in (Google) — go straight to checkout
            setLoading(1);
            loadCheckout(priceId, user.uid);
        } else {
            setShowPlans(0);
        }
    };

    const onSubmit = async (e) => {
        e.preventDefault()

        if(password!==confirmPassword) {
            setError(`Passwords don't match!`)
            return;
        }
        
        setError('')
        setLoading(1)
        
        try {
            let captchaVerified = false;
            
            // Try v3 first (invisible)
            if (executeRecaptcha && !showV2Captcha) {
                try {
                    const captchaToken = await executeRecaptcha('signup');
                    const captchaResponse = await api.post('/verify-captcha', { token: captchaToken, action: 'signup' });
                    captchaVerified = captchaResponse.data.success;
                } catch (v3Error) {
                    console.warn('reCAPTCHA v3 failed, falling back to v2:', v3Error);
                }
            }
            
            // If v3 failed, show v2 checkbox
            if (!captchaVerified && !showV2Captcha) {
                setLoading(0);
                setShowV2Captcha(true);
                setError('Please complete the security check below.');
                return;
            }
            
            // If v2 is showing, use v2 token
            if (showV2Captcha) {
                if (!v2Token) {
                    setLoading(0);
                    setError('Please complete the reCAPTCHA checkbox.');
                    return;
                }
                // Verify v2 token
                const captchaResponse = await api.post('/verify-captcha', { token: v2Token, action: 'signup', version: 'v2' });
                if (!captchaResponse.data.success) {
                    setLoading(0);
                    setError('reCAPTCHA verification failed. Please try again.');
                    recaptchaV2Ref.current?.reset();
                    setV2Token(null);
                    return;
                }
            }

            let user = await signUp(email, password);
            await verificationEmail();

            if (isFreeTier) {
                // Free tier — skip Stripe, go to app
                navigate('/');
            } else {
                // Paid tier — proceed to Stripe checkout
                await loadCheckout(selectedPlan, user.uid)
            }
        } catch (error) {
            setLoading(0)
            setError(getAuthErrorMessage(error))
            console.log(error.code, error.message);
            // Reset v2 captcha on error
            if (showV2Captcha) {
                recaptchaV2Ref.current?.reset();
                setV2Token(null);
            }
        }
    }
    
    const onV2CaptchaChange = (token) => {
        setV2Token(token);
        if (token) {
            setError(''); // Clear error when user completes captcha
        }
    }

    const onSubmitWithGoogle = async () => {
        setError('')
        setLoading(1)
        try {
            await signInWithGoogle()
            // Google user is now authenticated, show plan selection
            setShowPlans(1)
            setLoading(0)
        } catch (error) {
            setLoading(0)
            setError(getAuthErrorMessage(error))
            console.log(error.code, error.message);
        }
    }

    const updateSecureTextEntry = () => {
        setSecureTextEntry(!secureTextEntry);
    }

    const updateSecureTextEntryConfirm = () => {
        setSecureTextEntryConfirm(!secureTextEntryConfirm);
    }

    const selectedPlanName = () => {
        if (isFreeTier) return 'Free';
        const plan = plans.find(p => p.prices.priceId === selectedPlan);
        return plan ? plan.name : '';
    };

    const selectedPlanPrice = () => {
        if (isFreeTier) return '$0';
        const plan = plans.find(p => p.prices.priceId === selectedPlan);
        return plan ? `$${plan.prices.priceData.unit_amount / 100}` : '';
    };

  return (
    <main>
        {
            loading?
            <Loading message={"Loading..."}/>
            :null
        }

        {
            showPlans===1?
            <ModalBackgroundStyled>
                <Products
                plans={plans}
                setShowPlans={handleClosePlans}
                onSelectFree={onSelectFree}
                onSelectPlan={onSelectPlan}
                loading={plans.length>0}
                />
            </ModalBackgroundStyled>
            :
            null
        }
        {/* Only show the form for non-authenticated users - Google sign-in users just see plan modal */}
        {!isAuthenticatedNoSub && (
        <FormSectionStyled>
            <FormContainerStyled>
                <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                    <h1 style={{ fontSize: '28px', fontWeight: '600', margin: '0 0 8px 0' }}>Create your account</h1>
                    <p style={{ fontSize: '15px', color: '#6b7280', margin: 0 }}>Start analyzing deals in minutes</p>
                </div>
                <form>
                    <InputContainerStyled>
                        <LabelStyled htmlFor="email-address">
                            Email address
                        </LabelStyled>
                        <InputStyled
                            id="email-address"
                            name="email"
                            type="email"
                            autoComplete="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            placeholder="Email address"
                        />
                        <InputIconStyled>{iconMap['User_Solid']}</InputIconStyled>
                    </InputContainerStyled>

                    <InputContainerStyled>
                        <LabelStyled htmlFor="password">
                            Password
                        </LabelStyled>
                        <InputStyled
                            id="password"
                            name="password"
                            type={secureTextEntry ? `password`: `text`}
                            autoComplete="new-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            placeholder="Password"
                        />
                        {<InputIconStyled $clickable onClick={updateSecureTextEntry}>{secureTextEntry?iconMap['Eye']:iconMap['Eye_Slash']}</InputIconStyled>}
                    </InputContainerStyled>
                    <InputContainerStyled>
                        <LabelStyled htmlFor="confirmPassword">
                            Confirm Password
                        </LabelStyled>
                        <InputStyled
                            id="confirmPassword"
                            name="confirmPassword"
                            type={secureTextEntryConfirm ? `password`: `text`}
                            autoComplete="new-password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            placeholder="Confirm Password"
                        />
                        {<InputIconStyled $clickable onClick={updateSecureTextEntryConfirm}>{secureTextEntryConfirm?iconMap['Eye']:iconMap['Eye_Slash']}</InputIconStyled>}
                    </InputContainerStyled>
                    {
                        (selectedPlan || isFreeTier)?
                        <PlanViewContainerStyled>
                            Selected Plan: {selectedPlanName()}
                            {'  '}{selectedPlanPrice()}
                            <PlansButton onClick={() => setShowPlans(1)}>Show plans</PlansButton>
                        </PlanViewContainerStyled>
                        :<div>Loading plans...</div>

                    }

                    {error!==''?<ErrorMessageStyled>{error}</ErrorMessageStyled>:null}

                    {showV2Captcha && (
                        <div style={{ display: 'flex', justifyContent: 'center', margin: '16px 0' }}>
                            <ReCAPTCHA
                                ref={recaptchaV2Ref}
                                sitekey={config.REACT_APP_RECAPTCHA_V2_SITE_KEY}
                                onChange={onV2CaptchaChange}
                                onExpired={() => setV2Token(null)}
                            />
                        </div>
                    )}

                    <ButtonContainerStyled>
                        <PrimaryButtonStyled
                            $grouped
                            type="submit"
                            onClick={onSubmit}
                        >
                            Sign up
                        </PrimaryButtonStyled>
                    </ButtonContainerStyled>

                    <OrContainerStyled><HrStyled/><p>OR</p><HrStyled/></OrContainerStyled>
                    <GoogleButtonContainerStyled>
                        <GoogleButtonStyled onClick={onSubmitWithGoogle}>Continue with Google</GoogleButtonStyled>
                    </GoogleButtonContainerStyled>
                </form>

                <p>
                    Already have an account?{' '}
                    <NavLinkWrapper to="/signin" >
                        Sign in
                    </NavLinkWrapper>
                </p>
            </FormContainerStyled>
        </FormSectionStyled>
        )}
    </main>
  )
}

export default SignUp
